import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { InternalInventory } from 'src/app/core/models/internal-inventory.model';
import { Location } from 'src/app/core/models/location.model';
import { SalesRep } from 'src/app/core/models/sales-rep.model';
import { UserType } from 'src/app/core/models/userType.models';
import { InternalInventoryService } from 'src/app/core/services/internal-inventory.service';
import { LocationService } from 'src/app/core/services/location.service';
import { LoginService } from 'src/app/core/services/login.service';
import { SalesRepService } from 'src/app/core/services/sales-rep.service';

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss']
})
export class InventoryComponent implements OnInit {
  inventory: InternalInventory = this.emptyInventory();
  routeMachines: {
    total: number;
    byState: Array<{ state: string; machines: number }>;
    locations: Array<{ state: string; name: string; salesRep: string; machines: number }>;
  } = { total: 0, byState: [], locations: [] };
  loading = true;
  saving = false;
  message = '';
  error = '';

  constructor(
    private inventoryService: InternalInventoryService,
    private locationService: LocationService,
    private salesRepService: SalesRepService,
    private loginService: LoginService,
    private router: Router
  ) {}

  get isRoot(): boolean {
    return this.loginService.userType === UserType.ROOT;
  }

  async ngOnInit(): Promise<void> {
    if (!this.isRoot) {
      await this.router.navigate(['/internal-dashboard']);
      return;
    }

    await this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const [inventory, routeMachines] = await Promise.all([
        this.inventoryService.getCurrent(),
        this.loadRouteMachines()
      ]);
      this.inventory = this.normalizeInventory(inventory);
      this.routeMachines = routeMachines;
    } catch {
      this.error = 'Inventory has not been loaded from the backend yet.';
      this.inventory = this.emptyInventory();
    } finally {
      this.loading = false;
    }
  }

  async save(): Promise<void> {
    if (!this.isRoot) return;
    this.saving = true;
    this.message = '';
    this.error = '';
    try {
      this.inventory = this.normalizeInventory(await this.inventoryService.save(this.buildSavePayload()));
      this.message = 'Inventory saved.';
    } catch {
      this.error = 'Could not save inventory. Confirm the backend /api/internal-inventory endpoint exists.';
    } finally {
      this.saving = false;
    }
  }

  get totalManualInventory(): number {
    return this.totalBoards + this.totalCabinets + this.totalKiosks;
  }

  get totalBoards(): number {
    return this.numberValue(this.inventory.txBoards) + this.numberValue(this.inventory.gaBoards);
  }

  get totalCabinets(): number {
    return this.numberValue(this.inventory.txCabinets) + this.numberValue(this.inventory.gaCabinets);
  }

  get totalKiosks(): number {
    return this.numberValue(this.inventory.txKiosks) + this.numberValue(this.inventory.gaKiosks);
  }

  private async loadRouteMachines(): Promise<{
    total: number;
    byState: Array<{ state: string; machines: number }>;
    locations: Array<{ state: string; name: string; salesRep: string; machines: number }>;
  }> {
    const [locations, salesReps] = await Promise.all([
      this.locationService.getAll(true),
      this.salesRepService.getAll()
    ]);
    const repsById = new Map((salesReps || []).map(rep => [this.normalizeId(rep.id), rep]));
    const totalsByState = new Map<string, number>();
    const countedLocations: Array<{ state: string; name: string; salesRep: string; machines: number }> = [];
    let total = 0;

    for (const location of locations || []) {
      const salesRep = repsById.get(this.normalizeId(location.salesRep?.id)) || location.salesRep;
      if (!this.isFortuneRouteLocation(location, salesRep)) continue;

      const state = this.normalizeState(location?.businessAddress?.state) || 'Unknown';
      if (this.isExcludedRouteState(state)) continue;

      const machineCount = this.getLocationMachineCount(location);
      if (!machineCount) continue;

      totalsByState.set(state, (totalsByState.get(state) || 0) + machineCount);
      total += machineCount;
      countedLocations.push({
        state,
        name: location.locationName || 'Unknown',
        salesRep: this.getSalesRepName(salesRep),
        machines: machineCount
      });
    }

    return {
      total,
      byState: Array.from(totalsByState.entries())
        .map(([state, machines]) => ({ state, machines }))
        .sort((a, b) => a.state.localeCompare(b.state, undefined, { numeric: true, sensitivity: 'base' })),
      locations: countedLocations.sort((a, b) => {
        const stateCompare = a.state.localeCompare(b.state, undefined, { numeric: true, sensitivity: 'base' });
        return stateCompare || a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
      })
    };
  }

  private isFortuneRouteLocation(location: Location, salesRep?: SalesRep): boolean {
    if (!location || this.isTruthy(location.salesOutlet) || this.isExplicitlyFalse(location.enabled)) return false;

    if (this.isWarehouseOrRepairLocation(location, salesRep)) return false;

    return !this.hasLeaseFlag(location) && !this.hasLeaseFlag(salesRep);
  }

  private getLocationMachineCount(location: Location): number {
    return this.numberValue(location?.numBoards || location?.numCabinets || 0);
  }

  private buildSavePayload(): InternalInventory {
    const inventory = this.normalizeInventory(this.inventory);
    return {
      ...inventory,
      boards: this.numberValue(inventory.txBoards) + this.numberValue(inventory.gaBoards),
      cabinets: this.numberValue(inventory.txCabinets) + this.numberValue(inventory.gaCabinets),
      kiosks: this.numberValue(inventory.txKiosks) + this.numberValue(inventory.gaKiosks)
    };
  }

  private normalizeInventory(inventory: Partial<InternalInventory> | null | undefined): InternalInventory {
    return {
      boards: this.numberValue(inventory?.boards),
      cabinets: this.numberValue(inventory?.cabinets),
      kiosks: this.numberValue(inventory?.kiosks),
      txBoards: this.numberValue(inventory?.txBoards),
      txCabinets: this.numberValue(inventory?.txCabinets),
      txKiosks: this.numberValue(inventory?.txKiosks),
      gaBoards: this.numberValue(inventory?.gaBoards),
      gaCabinets: this.numberValue(inventory?.gaCabinets),
      gaKiosks: this.numberValue(inventory?.gaKiosks),
      updatedAt: inventory?.updatedAt,
      updatedBy: inventory?.updatedBy
    };
  }

  private emptyInventory(): InternalInventory {
    return {
      boards: 0,
      cabinets: 0,
      kiosks: 0,
      txBoards: 0,
      txCabinets: 0,
      txKiosks: 0,
      gaBoards: 0,
      gaCabinets: 0,
      gaKiosks: 0
    };
  }

  private normalizeState(value?: string | null): string {
    const state = (value || '').trim().toUpperCase();
    if (state === 'TEXAS') return 'TX';
    if (state === 'GEORGIA') return 'GA';
    if (state === 'PUERTO RICO') return 'PR';
    if (state === 'WISCONSIN') return 'WI';
    return state;
  }

  private isExcludedRouteState(state: string): boolean {
    return state === 'PR' || state === 'WI' || state === 'Unknown';
  }

  private normalizeId(value?: string | null): string {
    return (value || '').toString().trim().toLowerCase();
  }

  private isWarehouseOrRepairLocation(location: Location, salesRep?: SalesRep): boolean {
    return [
      location?.locationName,
      location?.locationTag,
      location?.businessAddress?.streetAddress1,
      location?.businessAddress?.streetAddress2,
      location?.businessAddress?.city,
      this.getSalesRepName(salesRep)
    ].some(value => this.isWarehouseOrRepairText(value));
  }

  private isWarehouseOrRepairText(value?: string | null): boolean {
    const normalized = this.normalizeName(value);
    const compact = normalized.replace(/[^a-z0-9]/g, '');
    return normalized.includes('warehouse') ||
      normalized.includes('repair') ||
      normalized.includes('service') ||
      compact === 'wh' ||
      compact.includes('whse') ||
      compact.includes('warehouse') ||
      compact.includes('repair');
  }

  private getSalesRepName(salesRep?: SalesRep | null): string {
    return `${salesRep?.firstName || ''} ${salesRep?.lastName || ''}`.trim() || 'Unassigned';
  }

  private normalizeName(value?: string | null): string {
    return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private isTruthy(value: unknown): boolean {
    return value === true || value === 1 || String(value).toLowerCase() === '1' || String(value).toLowerCase() === 'true';
  }

  private isExplicitlyFalse(value: unknown): boolean {
    return value === false || value === 0 || String(value).toLowerCase() === '0' || String(value).toLowerCase() === 'false';
  }

  private hasLeaseFlag(value: unknown): boolean {
    const source = value as any;
    return this.isTruthy(source?.tenDollarEnabled) ||
      this.isTruthy(source?.fiveDollarEnabled) ||
      this.isTruthy(source?.is10ADay) ||
      this.isTruthy(source?.is10aDay) ||
      this.isTruthy(source?.is10aday) ||
      this.isTruthy(source?.tenDollar) ||
      this.isTruthy(source?.is5ADay) ||
      this.isTruthy(source?.is5aDay) ||
      this.isTruthy(source?.is5aday) ||
      this.isTruthy(source?.fiveDollar);
  }

  private numberValue(value: unknown): number {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }
}
